import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { UserMinus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { useState, useEffect } from "react";

interface FollowersFollowingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileUserId: string;
  initialTab?: "followers" | "following";
}

export const FollowersFollowingDialog = ({

  open,
  onOpenChange,
  profileUserId,
  initialTab = "followers",
}: FollowersFollowingDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  // Helper function for avatar URL - handles URLs that may already have cache buster
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    // If URL already has a timestamp, use it as-is
    if (avatarUrl.includes('?t=') || avatarUrl.includes('&t=')) {
      return avatarUrl;
    }
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${Date.now()}`;
  };

  // Fetch blocked users
  const { data: blockedUserIds = [] } = useQuery({
    queryKey: ["blocked-users-viewer"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", user.id);
      
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", user.id);
      
      if (error1 || error2) throw error1 || error2;
      
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      return [...new Set([...blockedByMe, ...blockedMe])];
    },
  });

  // Fetch followers
  const { data: followers = [], isLoading: loadingFollowers } = useQuery({
    queryKey: ["profile-followers", profileUserId, blockedUserIds],
    queryFn: async () => {
      // 1) fetch follow rows (no joins)
      const { data: followRows, error: followErr } = await supabase
        .from("follows")
        .select("id, follower_id, created_at")
        .eq("following_id", profileUserId);

      if (followErr) throw followErr;
      if (!followRows?.length) return [];

      // 2) filter out blocked users
      const filtered = followRows.filter((f: any) => !blockedUserIds.includes(f.follower_id));
      if (!filtered.length) return [];

      // 3) filter out soft-deleted users
      const userIds = filtered.map((f: any) => f.follower_id);
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      const activeSet = new Set((activeUserIds as string[]) || []);
      const activeFiltered = filtered.filter((f: any) => activeSet.has(f.follower_id));
      if (!activeFiltered.length) return [];

      // 4) fetch profiles for those follower ids
      const activeIds = activeFiltered.map((f: any) => f.follower_id);
      const { data: profiles, error: profErr } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", activeIds);
      if (profErr) throw profErr;

      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      return activeFiltered.map((f: any) => ({ ...f, follower: profileMap.get(f.follower_id) }));
    },
    enabled: open,
  });

  // Fetch following
  const { data: following = [], isLoading: loadingFollowing } = useQuery({
    queryKey: ["profile-following", profileUserId, blockedUserIds],
    queryFn: async () => {
      // 1) fetch follow rows (no joins)
      const { data: followRows, error: followErr } = await supabase
        .from("follows")
        .select("id, following_id, created_at")
        .eq("follower_id", profileUserId);

      if (followErr) throw followErr;
      if (!followRows?.length) return [];

      // 2) filter out blocked users
      const filtered = followRows.filter((f: any) => !blockedUserIds.includes(f.following_id));
      if (!filtered.length) return [];

      // 3) filter out soft-deleted users
      const userIds = filtered.map((f: any) => f.following_id);
      const { data: activeUserIds } = await (supabase as any).rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      const activeSet = new Set((activeUserIds as string[]) || []);
      const activeFiltered = filtered.filter((f: any) => activeSet.has(f.following_id));
      if (!activeFiltered.length) return [];

      // 4) fetch profiles for those following ids
      const activeIds = activeFiltered.map((f: any) => f.following_id);
      const { data: profiles, error: profErr } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", activeIds);
      if (profErr) throw profErr;

      const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
      return activeFiltered.map((f: any) => ({ ...f, following: profileMap.get(f.following_id) }));
    },
    enabled: open,
  });

  // Mutation to remove follower
  const removeFollower = useMutation({
    mutationFn: async (followerId: string) => {
      const { data, error } = await supabase.rpc("remove_follower", {
        p_follower_id: followerId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to remove follower");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-followers", profileUserId] });
      queryClient.invalidateQueries({ queryKey: ["followers", currentUserId] });
      toast.success(t('notifications.toast.follower_removed'));
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove follower");
    },
  });

  const renderUserCard = (user: any, actionButton?: React.ReactNode) => {
    if (!user) return null;

    return (
      <div
        key={user.id}
        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
      >
        <Avatar 
          className="h-10 w-10 cursor-pointer"
          onClick={() => {
            onOpenChange(false);
            navigate(`/${user.username}`);
          }}
        >
          <AvatarImage 
            src={getAvatarUrl(user.avatar_url)}
            alt={user.username || 'User'}
            className="object-cover"
            loading="lazy"
          />
          <AvatarFallback className="bg-primary/10 text-primary">
            {user.username?.[0] || "?"}
          </AvatarFallback>
        </Avatar>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => {
            onOpenChange(false);
            navigate(`/${user.username}`);
          }}
        >
          <p className="font-medium truncate">
            {user.username}
          </p>
          <p className="text-sm text-muted-foreground truncate">
            @{user.username}
          </p>
        </div>
        {actionButton}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ui.text.followers_following')}</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="followers">
              {t('social.follows.followers_count', { count: followers.length })}
            </TabsTrigger>
            <TabsTrigger value="following">
              {t('social.follows.following_count', { count: following.length })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="followers" className="mt-4 max-h-96 overflow-y-auto space-y-1">
            {loadingFollowers ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('ui.text.loading')}</p>
            ) : followers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('ui.text.no_followers_yet')}</p>
            ) : (
              followers.map((follow: any) => {
                const isOwnProfile = currentUserId === profileUserId;
                return renderUserCard(
                  follow.follower,
                  isOwnProfile ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFollower.mutate(follow.follower_id);
                      }}
                      disabled={removeFollower.isPending}
                    >
                      <UserMinus className="w-4 h-4 mr-1" />
                      {t('social.follows.remove')}
                    </Button>
                  ) : undefined
                );
              })
            )}
          </TabsContent>

          <TabsContent value="following" className="mt-4 max-h-96 overflow-y-auto space-y-1">
            {loadingFollowing ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('ui.text.loading')}</p>
            ) : following.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('ui.text.not_following_anyone_yet')}</p>
            ) : (
              following.map((follow: any) => renderUserCard(follow.following))
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
